/**
 * Product Optimized Pricing Handler
 * Updates prices when variant changes for product-optimized template
 */
class ProductOptimizedPricing extends HTMLElement {
  constructor() {
    super();
    this.productData = null;
    this.currentVariant = null;
    this.subscriptionDiscount = null;
    this.oneTimeDiscount = null;
    this.sitewideSale = null;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Get product data from JSON script tag
    const productJsonEl = document.querySelector('[id^="product-json-"]');
    if (productJsonEl) {
      try {
        this.productData = JSON.parse(productJsonEl.textContent);
      } catch (e) {
        console.error('ProductOptimizedPricing: Failed to parse product JSON', e);
        return;
      }
    }

    // Get sitewide sale config from window
    this.sitewideSale = window.moonjuiceSitewideSale || {};

    // Check if this product is excluded from sitewide sale
    const isExcluded = this.checkSaleExclusion();

    // Get section settings for overrides
    this.loadSectionSettings();

    // Listen for variant changes
    document.addEventListener('variant:change', (event) => {
      if (event.detail && event.detail.variant) {
        this.handleVariantChange(event.detail.variant);
      }
    });

    // Set initial variant pricing
    const initialVariantId = document.querySelector('.product-variant-id')?.value;
    if (initialVariantId && this.productData) {
      const initialVariant = this.productData.variants.find(v => v.id === parseInt(initialVariantId));
      if (initialVariant) {
        this.handleVariantChange(initialVariant);
      }
    }
  }

  /**
   * Check if product is excluded from sitewide sale
   */
  checkSaleExclusion() {
    if (!this.productData) return true;

    const excludedTags = this.sitewideSale.excludedTags || [];
    const excludedVariantIds = this.sitewideSale.excludedVariantIds || [];

    // Check product tags
    const productTags = this.productData.tags || [];
    if (excludedTags.some(tag => productTags.includes(tag))) {
      return true;
    }

    return false;
  }

  /**
   * Load section settings for price overrides
   */
  loadSectionSettings() {
    // Check for override data in hidden divs rendered by Liquid
    const subscriptionOverridesEl = document.querySelector('[data-variant-subscription-overrides]');
    const oneTimeOverridesEl = document.querySelector('[data-variant-one-time-overrides]');

    if (subscriptionOverridesEl) {
      this.subscriptionDiscount = {};
      subscriptionOverridesEl.querySelectorAll('[data-variant-id]').forEach(el => {
        const variantId = el.dataset.variantId;
        const discount = parseFloat(el.dataset.subscriptionDiscount);
        if (variantId && !isNaN(discount)) {
          this.subscriptionDiscount[variantId] = discount;
        }
      });
    }

    if (oneTimeOverridesEl) {
      this.oneTimeDiscount = {};
      oneTimeOverridesEl.querySelectorAll('[data-variant-id]').forEach(el => {
        const variantId = el.dataset.variantId;
        const discount = parseFloat(el.dataset.oneTimeDiscount);
        if (variantId && !isNaN(discount)) {
          this.oneTimeDiscount[variantId] = discount;
        }
      });
    }
  }

  /**
   * Handle variant change and update prices
   */
  handleVariantChange(variant) {
    // Get full variant data from product JSON (includes selling_plan_allocations)
    const fullVariant = this.getFullVariantData(variant.id);
    if (!fullVariant) {
      console.warn('ProductOptimizedPricing: Could not find full variant data for', variant.id);
      return;
    }

    this.currentVariant = fullVariant;

    // Calculate prices
    const prices = this.calculatePrices(fullVariant);

    // Update DOM
    this.updateSubscriptionPrice(prices);
    this.updateOneTimePrice(prices);
    this.updateSubscriptionMessage(prices);
  }

  /**
   * Get full variant data from product JSON
   */
  getFullVariantData(variantId) {
    if (!this.productData || !this.productData.variants) return null;

    return this.productData.variants.find(v => v.id === variantId);
  }

  /**
   * Calculate all prices for the variant
   */
  calculatePrices(variant) {
    let prices = {
      oneTimePrice: variant.price,
      oneTimeCompareAt: variant.compare_at_price || variant.price,
      subscriptionPrice: null,
      subscriptionCompareAt: null,
      subscriptionSavings: 0,
      subscriptionDiscountPercent: 0
    };

    // Get first Recharge selling plan allocation
    const rechargeAllocation = this.getRechargeAllocation(variant);

    if (rechargeAllocation) {
      prices.subscriptionPrice = rechargeAllocation.price;
      prices.subscriptionCompareAt = rechargeAllocation.compare_at_price || variant.price;

      // Get subscription discount percentage
      const priceAdjustment = rechargeAllocation.selling_plan?.price_adjustments?.[0];
      if (priceAdjustment) {
        prices.subscriptionDiscountPercent = priceAdjustment.value || 0;
      }
    }

    // Apply metafield overrides if present
    if (this.subscriptionDiscount && this.subscriptionDiscount[variant.id]) {
      const discountPercent = this.subscriptionDiscount[variant.id];
      prices.subscriptionPrice = Math.round(variant.price * (1 - discountPercent / 100));
      prices.subscriptionDiscountPercent = discountPercent;
    }

    if (this.oneTimeDiscount && this.oneTimeDiscount[variant.id]) {
      const discountPercent = this.oneTimeDiscount[variant.id];
      prices.oneTimeCompareAt = variant.price;
      prices.oneTimePrice = Math.round(variant.price * (1 - discountPercent / 100));
    }

    // Apply sitewide sale if applicable
    const isExcluded = this.checkSaleExclusion() ||
                       this.sitewideSale.excludedVariantIds?.includes(variant.id);

    if (!isExcluded) {
      prices = this.applySitewideSale(prices, variant);
    }

    // Calculate savings
    if (prices.subscriptionPrice) {
      prices.subscriptionSavings = prices.subscriptionCompareAt - prices.subscriptionPrice;

      // Recalculate discount percent based on actual prices
      if (prices.subscriptionCompareAt > 0) {
        prices.subscriptionDiscountPercent = Math.round(
          (1 - prices.subscriptionPrice / prices.subscriptionCompareAt) * 100
        );
      }
    }

    // Round up prices (ceiling to nearest dollar)
    prices.oneTimePrice = Math.ceil(prices.oneTimePrice / 100) * 100;
    prices.oneTimeCompareAt = Math.ceil(prices.oneTimeCompareAt / 100) * 100;
    if (prices.subscriptionPrice) {
      prices.subscriptionPrice = Math.ceil(prices.subscriptionPrice / 100) * 100;
      prices.subscriptionCompareAt = Math.ceil(prices.subscriptionCompareAt / 100) * 100;
      prices.subscriptionSavings = Math.floor(prices.subscriptionSavings / 100) * 100;
    }

    return prices;
  }

  /**
   * Get Recharge selling plan allocation for variant
   */
  getRechargeAllocation(variant) {
    if (!variant.selling_plan_allocations || variant.selling_plan_allocations.length === 0) {
      return null;
    }

    // Get the first allocation - in Shopify JSON, allocations don't have nested selling_plan object
    // They only have selling_plan_id and selling_plan_group_id
    // We need to match this to the product's selling_plan_groups to get full details
    const allocation = variant.selling_plan_allocations[0];

    if (!allocation) {
      return null;
    }

    // Find the matching selling plan from product data
    const sellingPlanGroupId = allocation.selling_plan_group_id;
    const sellingPlanId = allocation.selling_plan_id;

    // Get selling plan groups from product
    const sellingPlanGroups = this.productData?.selling_plan_groups || [];
    const matchingGroup = sellingPlanGroups.find(group => group.id === sellingPlanGroupId);

    if (!matchingGroup) {
      // Return allocation anyway - it has price data we can use
      return allocation;
    }

    // Find the specific selling plan within the group
    const sellingPlan = matchingGroup.selling_plans?.find(plan => plan.id === sellingPlanId);

    if (sellingPlan) {
      // Attach the full selling plan to the allocation for easier access
      allocation.selling_plan = sellingPlan;
    }

    return allocation;
  }

  /**
   * Apply sitewide sale discounts
   */
  applySitewideSale(prices, variant) {
    const sale = this.sitewideSale;

    // Apply subscription discount if enabled
    if (sale.subscriptionEnabled && sale.subscriptionDiscount > 0) {
      const multiplier = (100 - sale.subscriptionDiscount) / 100;

      // Store original compare price as MSRP
      const msrp = variant.compare_at_price > 0 ? variant.compare_at_price : variant.price;
      prices.subscriptionCompareAt = msrp;
      prices.subscriptionPrice = Math.round(msrp * multiplier);
      prices.subscriptionDiscountPercent = sale.subscriptionDiscount;
    }

    // Apply one-time discount if enabled
    if (sale.onetimeEnabled && sale.onetimeDiscount > 0) {
      const multiplier = (100 - sale.onetimeDiscount) / 100;

      // Store original price as compare price
      const msrp = variant.compare_at_price > 0 ? variant.compare_at_price : variant.price;
      prices.oneTimeCompareAt = msrp;
      prices.oneTimePrice = Math.round(msrp * multiplier);
    }

    return prices;
  }

  /**
   * Update subscription price display
   */
  updateSubscriptionPrice(prices) {
    if (!prices.subscriptionPrice) return;

    // Update main subscription price
    const subscriptionPriceEl = document.querySelector('#first_sub_price');
    if (subscriptionPriceEl) {
      subscriptionPriceEl.textContent = this.formatMoney(prices.subscriptionPrice);
    }

    // Update compare at price (strikethrough)
    const delPriceEl = document.querySelector('#del_price');
    if (delPriceEl) {
      if (prices.subscriptionPrice < prices.subscriptionCompareAt) {
        delPriceEl.textContent = this.formatMoney(prices.subscriptionCompareAt);
        delPriceEl.style.display = '';
      } else {
        delPriceEl.textContent = '';
        delPriceEl.style.display = 'none';
      }
    }

    // Update savings display
    const savingsContainerEl = document.querySelector('#sub_price');
    const savingsAmountEl = document.querySelector('#sub_inner_price');

    if (savingsContainerEl && savingsAmountEl) {
      if (prices.subscriptionSavings > 0) {
        savingsAmountEl.textContent = this.formatMoney(prices.subscriptionSavings);
        savingsContainerEl.classList.remove('hidden');
      } else {
        savingsContainerEl.classList.add('hidden');
      }
    }
  }

  /**
   * Update one-time price display
   */
  updateOneTimePrice(prices) {
    // Update one-time price
    const oneTimePriceEl = document.querySelector('#one_time_price_wrap');
    if (oneTimePriceEl) {
      oneTimePriceEl.textContent = this.formatMoney(prices.oneTimePrice);
    }

    // Update compare at price
    const oneTimePriceContainer = document.querySelector('#one_time_price');
    if (oneTimePriceContainer) {
      const compareEl = oneTimePriceContainer.querySelector('.compare-price');

      if (prices.oneTimeCompareAt > prices.oneTimePrice) {
        if (compareEl) {
          compareEl.textContent = this.formatMoney(prices.oneTimeCompareAt);
          compareEl.style.display = '';
        } else {
          // Create compare price element if it doesn't exist
          const newCompareEl = document.createElement('span');
          newCompareEl.className = 'compare-price';
          newCompareEl.textContent = this.formatMoney(prices.oneTimeCompareAt);
          oneTimePriceContainer.appendChild(newCompareEl);
        }
      } else if (compareEl) {
        compareEl.style.display = 'none';
      }
    }
  }

  /**
   * Update subscription message with discount percentage
   */
  updateSubscriptionMessage(prices) {
    if (!prices.subscriptionPrice) return;

    const shippingInfoEl = document.querySelector('.shipping-info');
    if (!shippingInfoEl) return;

    // Get the message text (excluding the popup)
    const messageNodes = Array.from(shippingInfoEl.childNodes).filter(
      node => node.nodeType === Node.TEXT_NODE
    );

    if (messageNodes.length > 0) {
      const currentMessage = messageNodes[0].textContent;

      // Replace [strSave] placeholder or percentage value
      const discountStr = `${prices.subscriptionDiscountPercent}%`;
      const updatedMessage = currentMessage.replace(/\d+%|\[strSave\]/, discountStr);

      messageNodes[0].textContent = updatedMessage;
    }
  }

  /**
   * Format money using Shopify's formatMoney
   */
  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      // Use appropriate format based on cents value
      const format = cents % 100 === 0 ? '${{amount_no_decimals}}' : '${{amount}}';
      return Shopify.formatMoney(cents, format);
    }

    // Fallback formatting
    const dollars = cents / 100;
    return cents % 100 === 0
      ? `$${Math.floor(dollars)}`
      : `$${dollars.toFixed(2)}`;
  }
}

// Register the custom element
if (!customElements.get('product-optimized-pricing')) {
  customElements.define('product-optimized-pricing', ProductOptimizedPricing);
}
